import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { MultiTextField } from '@/components/form/fields/text/multi/MultiTextInput';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useInvitesToPartner } from '@/services/api/hooks/invites';
import { usePartnerUsers } from '@/services/api/hooks/partners';
import { zodResolver } from '@hookform/resolvers/zod';

import { FunctionComponent, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const InviteToMSPFormFields = z.object({
  emails: z.array(z.string())
});

type InviteToMSPFormFields = z.infer<typeof InviteToMSPFormFields>;

const InviteUsersToMspModal: FunctionComponent<ModalProps<{ mspId: string }>> = props => {
  const InviteUsersToMsp = () => {
    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing partner id');

    const [users] = usePartnerUsers(mspId);

    let domain: string = '';
    if (users.items.length > 0) {
      const emailParts = users.items[0].email.split('@');
      if (typeof emailParts === 'string' || emailParts.length !== 2) throw new Error('Invalid email');
      domain = emailParts[1];
    }

    const [domainError, setDomainError] = useState<string | undefined>(undefined);

    const invitesToPartner = useInvitesToPartner();
    const methods = useForm<InviteToMSPFormFields>({
      resolver: zodResolver(InviteToMSPFormFields)
    });

    const onSubmit = async (data: InviteToMSPFormFields) => {
      if (domain && data.emails.some(email => !email.endsWith(domain))) {
        setDomainError("You can only invite users with the same email domain.");
        return;
      }

      const results = await invitesToPartner.mutateAsync({
        partnerId: mspId,
        emails: data.emails
      });

      if (Array.isArray(results) && results.length) {
        props.close();
      }
    };

    const errorObj = invitesToPartner.error as Error | undefined;
    const error = errorObj ? errorObj.message : domainError ?? '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Invite users to MSP</h1>
        </Modal.Controls>

        <Form className="flex w-full flex-col space-y-4 py-3" methods={methods} onSubmit={onSubmit}>
          <MultiTextField label="Email addresses" id="emails" />

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end pt-2">
            <Button
              loading={invitesToPartner.isLoading}
              className="w-fit"
              variant={ButtonVariant.Primary}
              type="submit"
            >
              Invite
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <InviteUsersToMsp />
      </Suspense>
    </Modal>
  );
};

export default InviteUsersToMspModal;
